// pages/TaskManagement/TasksIveAssigned.js
//
// Legacy menu: "View Tasks I've Assigned"
// Round TM-10 (2026) — legacy flag semantics (audit Deliverable 10):
//   Open     = OriginatorMarkComplete=0, Rejected=0, not Meeting
//   Rejected = the rejected-assignment rows (reassign from the task page)
//   Closed   = OriginatorMarkComplete=1
// Rows exclude self-assignments; totals are the assignee's own
// user-stated labor.
// Backend: GET /api/TaskManagement/assigned-by-me?tab=
//
// Document Control 2026-10-21 ("3 tasks assigned for ECN-20611, only one
// should be open", "CSV returns a blank page", "column filters instead of
// a search button"):
//   - Task # and Status columns. Open keeps a task until ITS CREATOR closes
//     it, so a finished assignment shows "Done, close it" instead of looking
//     like unfinished work.
//   - the Document column carries the revision: an ECN with two revisions
//     has tasks on both, and the ECN's Tasks tab shows one revision at a time.
//   - a filter box under every column (client side, instant).
//   - CSV fixed: the helper takes (filename, columns, rows); the page passed
//     the rows as the columns, so the file had an empty header and nothing
//     else, and without ".csv" the browser opened it as a blank page.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader, EmptyState, ResponsiveTabs, TableSkeleton,
    ColumnFilterRow, applyColumnFilters,
} from "../../Components/Common/DashboardKit";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import api from "../../api";

const TABS = [
    { key: "open", label: "Open" },
    { key: "rejected", label: "Rejected" },
    { key: "closed", label: "Closed" },
];

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const fmtMins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const docText = (r) => (r.dmsDocId ? `${r.dmsDocId}${r.dmsRev != null ? ` rev ${r.dmsRev}` : ""}` : "");

// What the row means for the person who assigned it.
const STATUS = {
    done: { label: "Done, close it", color: "success", help: "The person finished. The task stays open until you close it." },
    wip: { label: "In progress", color: "info", help: "Time has been logged, not finished yet." },
    new: { label: "Not started", color: "secondary", help: "No time logged yet." },
    rejected: { label: "Rejected", color: "danger", help: "Rejected by the person. Reassign from the task page." },
    closed: { label: "Closed", color: "dark", help: "You closed this task." },
};
const statusKey = (r, tab) => {
    if (tab === "rejected") return "rejected";
    if (tab === "closed") return "closed";
    if (r.assigneeDone) return "done";
    return (r.activitiesLogged || 0) > 0 ? "wip" : "new";
};

const TasksIveAssigned = () => {
    document.title = "Tasks I've Assigned || SSW Technologies Dashboard";
    const navigate = useNavigate();

    const [tab, setTab] = useState("open");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState({});

    const fetchRows = useCallback(async (tabKey) => {
        try {
            setLoading(true);
            setError("");
            const res = await api.get("/TaskManagement/assigned-by-me", {
                params: { tab: tabKey },
            });
            const data = res?.data ?? res;
            setRows(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
        } catch (err) {
            setRows([]);
            setError(err?.response?.data?.message || "Unable to load tasks.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRows(tab); }, [tab, fetchRows]);

    const statusOptions = useMemo(() => {
        const keys = tab === "open" ? ["done", "wip", "new"] : tab === "rejected" ? ["rejected"] : ["closed"];
        return keys.map((k) => ({ value: k, label: STATUS[k].label }));
    }, [tab]);

    const filterColumns = useMemo(() => [
        { id: "created", type: "text", placeholder: "Date", get: (r) => fmtDate(r.createdOn) },
        { id: "taskId", type: "text", placeholder: "Task #", get: (r) => String(r.taskId ?? "") },
        { id: "assignToName", type: "text", placeholder: "Name" },
        { id: "description", type: "text", placeholder: "Words in the description" },
        { id: "document", type: "text", placeholder: "e.g. 20611", get: docText },
        { id: "status", type: "select", options: statusOptions, get: (r) => statusKey(r, tab) },
        { id: "activities", type: "none" },
        { id: "time", type: "none" },
    ], [statusOptions, tab]);

    const shown = useMemo(() => applyColumnFilters(rows, filterColumns, filters), [rows, filterColumns, filters]);
    const filtering = Object.values(filters).some((v) => v != null && v !== "");

    const exportCsv = () => exportRowsToCsv(
        `tasks-ive-assigned-${tab}.csv`,
        [
            { key: "taskId", label: "Task #" },
            { label: "Created", get: (r) => fmtDate(r.createdOn) },
            { key: "assignToName", label: "Assigned To" },
            { key: "description", label: "Description" },
            { label: "Document", get: (r) => r.dmsDocId || "" },
            { label: "Revision", get: (r) => (r.dmsRev != null ? r.dmsRev : "") },
            { label: "Status", get: (r) => STATUS[statusKey(r, tab)].label },
            { key: "activitiesLogged", label: "Activities" },
            { label: "Total Time", get: (r) => fmtMins(r.totalTimeMinutes) },
            { key: "totalTimeMinutes", label: "Total Minutes" },
        ],
        shown
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-send-plane-line"
                    title="Tasks I've Assigned"
                    subtitle="Everything you created, by assignee"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Tasks I've Assigned" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <ResponsiveTabs tabs={TABS} activeKey={tab} onChange={(k) => { setTab(k); setFilters({}); }}   /* Round DMS-IssueRound-1d: ResponsiveTabs takes onChange — onSelect was never fired, so the tabs were dead */ />
                            <div className="d-flex align-items-center gap-2">
                                <Badge color="light" className="text-muted">
                                    {filtering ? `${shown.length} of ${rows.length}` : rows.length}
                                </Badge>
                                {filtering && (
                                    <Button size="sm" color="light" onClick={() => setFilters({})}>Clear filters</Button>
                                )}
                                <Button size="sm" color="light" onClick={exportCsv} disabled={shown.length === 0} title="Download the rows shown as a CSV file for Excel">
                                    <i className="ri-download-2-line align-bottom me-1"></i>CSV
                                </Button>
                                <Button size="sm" color="light" onClick={() => fetchRows(tab)}>
                                    <i className="ri-refresh-line align-bottom"></i>
                                </Button>
                            </div>
                        </div>

                        {tab === "open" && !loading && rows.length > 0 && (
                            <div className="text-muted small mb-2">
                                Open means you have not closed the task yet. A row marked <strong>Done, close it</strong> is
                                finished by the person. A task of an earlier revision of a document stays here until it is closed.
                            </div>
                        )}

                        {loading ? (
                            <TableSkeleton rows={8} cols={8} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-inbox-line"
                                title="No tasks"
                                message="Nothing in this tab. Rejected tasks can be reassigned from the task page."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {/* Round DMS-IssueRound-1c: Created leads, Due removed,
                                                Document (DMS relationship) added */}
                                            <th>Created</th>
                                            <th>Task #</th>
                                            <th>Assigned To</th>
                                            <th>Description</th>
                                            <th>Document</th>
                                            <th>Status</th>
                                            <th className="text-end">Activities</th>
                                            <th className="text-end">Total Time</th>
                                        </tr>
                                        <ColumnFilterRow columns={filterColumns} filters={filters} onChange={setFilters} live />
                                    </thead>
                                    <tbody>
                                        {shown.length === 0 && (
                                            <tr><td colSpan={8} className="text-muted text-center py-3">No rows match the filters.</td></tr>
                                        )}
                                        {shown.map((r, i) => {
                                            const st = STATUS[statusKey(r, tab)];
                                            return (
                                                <tr
                                                    key={`${r.taskId}-${r.assignTo}-${i}`}
                                                    role="button"
                                                    onClick={() => navigate(`/task-management/view-task/${r.taskId}`)}
                                                    title={`Open Task #${r.taskId}`}
                                                >
                                                    <td className="fw-semibold">{fmtDate(r.createdOn)}</td>
                                                    <td className="text-muted">{r.taskId}</td>
                                                    <td>{r.assignToName}</td>
                                                    <td className="text-truncate" style={{ maxWidth: "420px" }}>{r.description}</td>
                                                    <td className="text-nowrap">
                                                        {r.dmsDocId && r.dmsId ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 text-decoration-none fw-semibold"
                                                                    title="Open the related DMS document"
                                                                    onClick={(e) => { e.stopPropagation(); navigate(`/document-management?openDmsId=${r.dmsId}`); }}
                                                                >
                                                                    {r.dmsDocId}
                                                                </button>
                                                                {r.dmsRev != null && <span className="text-muted small ms-1">rev {r.dmsRev}</span>}
                                                            </>
                                                        ) : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td><Badge color={st.color} pill title={st.help}>{st.label}</Badge></td>
                                                    <td className="text-end">{r.activitiesLogged}</td>
                                                    <td className="text-end">{fmtMins(r.totalTimeMinutes)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TasksIveAssigned;
